import { useState } from 'react';
import { Button, Divider, Stack, Text } from '@mantine/core';
import { IconBuildingChurch } from '@tabler/icons-react';
import { useAuth } from '@actschurch/ccms-sdk';
import { Callout } from '@/components/ui/Callout';
import { usePortalSettings } from '@/lib/settings';
import { planningCentreCallbackUrl, rememberPlanningCentreRedirect } from '@/lib/planningCentre';

export interface PlanningCentreButtonProps {
  /** Where to land after signing in (e.g. /p/ABC123). */
  redirectTo: string;
  /** Divider under the button ("or use your email address") for the sign-in page. */
  withDivider?: boolean;
  /** Primary (filled) styling, for screens where this is the main action. */
  primary?: boolean;
}

/**
 * "Sign in with Planning Center": the same flow as ccms_v2, the Resource Centre and Carpool. Hidden
 * when the portal settings switch planningCentreLoginEnabled off. New Planning Center people get a
 * CCMS account automatically; an existing account with the same email is linked after the person
 * confirms its password on the callback page.
 */
export function PlanningCentreButton({ redirectTo, withDivider, primary }: PlanningCentreButtonProps) {
  const { planningCentre } = useAuth();
  const { settings } = usePortalSettings();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!settings.planningCentreLoginEnabled) return null;

  const onClick = async () => {
    setStarting(true);
    setError(null);
    try {
      rememberPlanningCentreRedirect(redirectTo);
      const { authUrl } = await planningCentre.start(redirectTo, planningCentreCallbackUrl());
      window.location.assign(authUrl);
    } catch {
      setError("We couldn't open Planning Center. Please check your signal and try again.");
      setStarting(false);
    }
  };

  return (
    <Stack gap="md" mb={withDivider ? 'md' : 0}>
      <Stack gap={6}>
        {error && <Callout tone="bad" live>{error}</Callout>}
        <Button
          variant={primary ? 'filled' : 'default'}
          size="lg"
          fullWidth
          leftSection={<IconBuildingChurch size={22} aria-hidden="true" />}
          loading={starting}
          onClick={onClick}
        >
          Sign in with Planning Center
        </Button>
        <Text size="sm" c="dimmed">Use the same login you use for Church Center or Planning Center.</Text>
      </Stack>
      {withDivider && <Divider label="or use your email address" labelPosition="center" />}
    </Stack>
  );
}
